"use client";

import { isDomCircularJsonSerializationError } from "./domSerializationError";
import { trackError } from "./trackError";
import { ErrorCard } from "@/components/error/ErrorCard";
import { useSystemMetrics } from "@/utils/systemMetrics/useSystemMetrics";
import { ErrorBoundary, FallbackRender } from "@sentry/nextjs";
import { useEffect } from "react";

const RELOAD_GUARD_WINDOW_MS = 30 * 1000;
const RELOAD_KEY_PREFIX = "chunkReloadLast:";

const getReloadStorageKey = () => {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return `${RELOAD_KEY_PREFIX}${path}`;
};

const attemptSessionGuardedReload = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const storage = window.sessionStorage;
    const key = getReloadStorageKey();
    const now = Date.now();
    const lastStr = storage.getItem(key);
    const last = lastStr ? parseInt(lastStr, 10) : 0;
    const allowed =
      !Number.isFinite(last) || now - last > RELOAD_GUARD_WINDOW_MS;
    // Write ahead of reload; if this fails, do not reload
    storage.setItem(key, String(now));
    if (allowed) {
      window.location.reload();
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

function serializeError(error: unknown): string {
  try {
    return JSON.stringify({
      name: (error as { name?: string })?.name ?? "",
      message: (error as { message?: string })?.message ?? "",
      stack: (error as { stack?: string })?.stack ?? "",
      type: (error as { type?: string })?.type ?? "",
      code: (error as { code?: string })?.code ?? "",
      ...(error as Record<string, unknown>),
    });
  } catch {
    return JSON.stringify({
      name: (error as { name?: string })?.name ?? "",
      message: (error as { message?: string })?.message ?? "",
      stack: (error as { stack?: string })?.stack ?? "",
      type: (error as { type?: string })?.type ?? "",
      code: (error as { code?: string })?.code ?? "",
    });
  }
}

/**
 * Detects network-related errors from Next.js router, webpack, and Turbopack.
 * This includes:
 * - Chunk loading failures (webpack/Turbopack)
 * - RSC (React Server Component) fetch failures during navigation
 * - Network errors during route segment loading
 * - RSC stream interruptions from the react-server-dom-{webpack,turbopack}
 *   client when the response stream closes before completing (transient
 *   mobile network blips, navigation away).
 */
export const isNetworkRelatedError = (error: unknown): boolean => {
  if (!error) return false;
  const content = serializeError(error);
  const message = ((error as { message?: string })?.message ?? "").trim();
  // React's RSC client throws exactly `new Error("Connection closed.")` when
  // the flight stream closes before completing. Source:
  // https://github.com/facebook/react/blob/74568e8627aa43469b74f2972f427a209639d0b6/packages/react-client/src/ReactFlightClient.js#L5386
  // The trailing period is part of the literal React message, so anchoring
  // on `Connection closed.` keeps us safe from other "connection closed"
  // strings we do want surfaced (e.g. ElevenLabs realtime audio's
  // `Connection closed during commit`).
  const isRscConnectionClosed = message === "Connection closed.";
  // Production React builds throw `Connection closed.` as minified error #412.
  const isMinifiedRscConnectionClosed = message.startsWith(
    "Minified React error #412;"
  );
  // Firefox's TypeError when a fetch response body fails mid-read.
  const isFirefoxBodyReadError = message === "Error in input stream";
  return (
    /Failed to load chunk/i.test(content) ||
    /Loading chunk/i.test(content) ||
    /ChunkLoadError/i.test(content) ||
    // Next.js router network errors (RSC fetch failures)
    /network error/i.test(content) ||
    // Safari/WebKit fetch failures (includes dynamic imports)
    /Load failed/i.test(content) ||
    isRscConnectionClosed ||
    isMinifiedRscConnectionClosed ||
    isFirefoxBodyReadError
  );
};

export const isIgnorableClientError = (error: unknown): boolean =>
  isNetworkRelatedError(error) || isDomCircularJsonSerializationError(error);

const ChunkErrorFallback = ({
  error,
  eventId,
}: {
  error: unknown;
  eventId?: string;
}) => {
  useEffect(() => {
    if (isNetworkRelatedError(error)) {
      void attemptSessionGuardedReload();
    }
  }, [error, eventId]);
  const isNetworkError = isNetworkRelatedError(error);

  return (
    <ErrorCard headerText="Unexpected error occurred" mustBeLoggedIn={false}>
      <div className="">
        {isNetworkError ? (
          <div>
            We're having trouble loading this page. This might be due to a
            temporary network issue.
          </div>
        ) : (
          <div>
            Please try again or contact support at{" "}
            <a href="mailto:support@listenlabs.ai">support@listenlabs.ai</a>.
          </div>
        )}
        {isNetworkError ? (
          <div className="mt-3 text-sm text-gray-700">
            <button
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-black/80"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        ) : null}
        <div className="mt-8">
          <p className="text-xs text-gray-600 text-left">
            <b>Url:</b>{" "}
            {typeof window === "undefined" ? "" : window.location.href} <br />
            <b>Event ID:</b> {eventId} <br />
            <b>Error:</b> {(error as { name?: string })?.name}:{" "}
            {(error as { message?: string })?.message}
          </p>
        </div>
      </div>
    </ErrorCard>
  );
};

export default function ErrorBoundaryWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const { track } = useSystemMetrics();
  const fallbackRenderer: FallbackRender = ({ error, eventId }) => {
    return <ChunkErrorFallback error={error} eventId={eventId} />;
  };

  return (
    <ErrorBoundary
      onError={(error, componentStack, eventId) => {
        const parsedError = {
          name: (error as { name?: string })?.name ?? "",
          message: (error as { message?: string })?.message ?? "",
          stack: (error as { stack?: string })?.stack ?? "",
          type: (error as { type?: string })?.type ?? "",
          code: (error as { code?: string })?.code ?? "",
        };
        if (!isIgnorableClientError(error)) {
          void trackError({
            error: parsedError,
            componentStack,
            eventId,
            errorPlace: "client-side error boundary",
          });
        } else if (isNetworkRelatedError(error)) {
          track({ name: "app.network_error", value: 1 });
        } else if (isDomCircularJsonSerializationError(error)) {
          track({ name: "app.dom_serialization_error", value: 1 });
        }
      }}
      fallback={fallbackRenderer}
    >
      {children}
    </ErrorBoundary>
  );
}
